<template>
  <div class="filters-container">
    <v-card class="mb-5 filter-card" elevation="3">
      <!-- Collapsible Header -->
      <v-card-title 
        class="primary--text d-flex justify-space-between filter-header" 
        @click="isExpanded = !isExpanded"
      >
        <div class="d-flex align-center">
          <v-icon color="primary" left class="mr-2">mdi-filter-variant</v-icon>
          <span class="text-h6">Filtres des offres de gaz</span>
        </div>
        <v-btn icon>
          <v-icon color="primary">{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-title>
      
      <!-- Collapsible Content -->
      <v-expand-transition>
        <div v-if="isExpanded">
          <v-divider></v-divider>
          
          <v-card-text class="pa-4">
            <v-row>
              <!-- Fournisseur Filter -->
              <v-col cols="12" md="6" lg="4">
                <v-autocomplete
                  v-model="selectedSupplier"
                  :items="suppliers"
                  label="Fournisseur"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-gas-cylinder"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>
              
              <!-- Contract Duration Filter -->
              <v-col cols="12" md="6" lg="4">
                <v-autocomplete
                  v-model="selectedDuration"
                  :items="durations"
                  label="Durée de contrat"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-calendar-range"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>
              
              <!-- Offer Type Filter -->
              <v-col cols="12" md="6" lg="4">
                <v-autocomplete
                  v-model="selectedOfferType"
                  :items="offerTypes"
                  label="Type d'offre"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-tag-outline"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>
              
              <!-- Meter Type Filter -->
              <v-col cols="12" md="6" lg="4">
                <v-autocomplete
                  v-model="selectedMeterType"
                  :items="meterTypes"
                  label="Type de compteur"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-meter-gas"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>
              
              <!-- Profile Filter -->
              <v-col cols="12" md="6" lg="4">
                <v-autocomplete
                  v-model="selectedProfile"
                  :items="profiles"
                  label="Profil de consommation"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-chart-bell-curve-cumulative"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>
              
              <!-- Sorting Section -->
              <v-col cols="12" class="mt-4">
                <p class="text-subtitle-1 font-weight-medium grey--text text--darken-2 mb-3">
                  <v-icon small class="mr-1">mdi-sort</v-icon>
                  Trier les offres par
                </p>
                
                <div class="d-flex flex-wrap">
                  <!-- Budget Sort Button Group -->
                  <div class="mr-4 mb-3">
                    <v-btn-toggle
                      v-model="budgetSortOption"
                      background-color="grey lighten-4"
                      dense
                      class="sort-toggle"
                      @change="onBudgetSortChange"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-btn
                              :value="null"
                              :color="budgetSortOption === null ? 'primary' : ''"
                              :outlined="budgetSortOption === null"
                              small
                              text
                            >
                              <v-icon small>mdi-currency-eur</v-icon>
                              <span class="ml-1">Budget</span>
                            </v-btn>
                          </div>
                        </template>
                        <span>Aucun tri</span>
                      </v-tooltip>
                      
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-btn
                              :value="'asc'"
                              :color="budgetSortOption === 'asc' ? 'primary' : ''"
                              :outlined="budgetSortOption === 'asc'"
                              small
                              text
                            >
                              <v-icon small>mdi-sort-ascending</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        <span>Trier par budget croissant</span>
                      </v-tooltip>
                      
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-btn
                              :value="'desc'"
                              :color="budgetSortOption === 'desc' ? 'primary' : ''"
                              :outlined="budgetSortOption === 'desc'"
                              small
                              text
                            >
                              <v-icon small>mdi-sort-descending</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        <span>Trier par budget décroissant</span>
                      </v-tooltip>
                    </v-btn-toggle>
                  </div>
                  
                  <!-- Commission Sort Button Group -->
                  <div class="mb-3">
                    <v-btn-toggle
                      v-model="commissionSortOption"
                      background-color="grey lighten-4"
                      dense
                      class="sort-toggle"
                      @change="onCommissionSortChange"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-btn
                              :value="null"
                              :color="commissionSortOption === null ? 'primary' : ''"
                              :outlined="commissionSortOption === null"
                              small
                              text
                            >
                              <v-icon small>mdi-cash</v-icon>
                              <span class="ml-1">Commission</span>
                            </v-btn>
                          </div>
                        </template>
                        <span>Aucun tri</span>
                      </v-tooltip>
                      
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-btn
                              :value="'asc'"
                              :color="commissionSortOption === 'asc' ? 'primary' : ''"
                              :outlined="commissionSortOption === 'asc'"
                              small
                              text
                            >
                              <v-icon small>mdi-sort-ascending</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        <span>Trier par commission croissante</span>
                      </v-tooltip>
                      
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-btn
                              :value="'desc'"
                              :color="commissionSortOption === 'desc' ? 'primary' : ''"
                              :outlined="commissionSortOption === 'desc'"
                              small
                              text
                            >
                              <v-icon small>mdi-sort-descending</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        <span>Trier par commission décroissante</span>
                      </v-tooltip>
                    </v-btn-toggle>
                  </div>
                </div>
              </v-col>
              
              <!-- Date Range Filters -->
              <v-col cols="12" md="6">
                <v-menu
                  ref="startDateMenu"
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="startDate"
                      label="Date de début"
                      prepend-inner-icon="mdi-calendar-start"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      hide-details
                      clearable
                      @click:clear="startDate = null"
                      class="filter-select"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    @input="startDateMenu = false"
                    color="primary"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-menu
                  ref="endDateMenu"
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="endDate"
                      label="Date de fin"
                      prepend-inner-icon="mdi-calendar-end"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      hide-details
                      clearable
                      @click:clear="endDate = null"
                      class="filter-select"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="endDate"
                    @input="endDateMenu = false"
                    color="primary"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
          
          <v-divider></v-divider>
          
          <v-card-actions class="pa-4 filter-actions">
            <v-spacer></v-spacer>
            <v-btn 
              text
              color="grey darken-1"
              @click="resetFilters"
              class="mr-3"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Réinitialiser
            </v-btn>
            <v-btn 
              color="primary" 
              @click="applyFilters"
              elevation="1"
            >
              <v-icon left>mdi-check</v-icon>
              Appliquer les filtres
            </v-btn>
          </v-card-actions>
        </div>
      </v-expand-transition>
      
      <!-- Indicator for active filters when collapsed -->
      <v-card-subtitle v-if="!isExpanded && hasActiveFilters" class="pa-3 active-filters-indicator">
        <div class="d-flex align-center">
          <v-chip
            color="primary"
            small
            class="mr-2"
            outlined
          >
            <v-icon left x-small>mdi-filter</v-icon>
            {{ activeFiltersCount }} filtres actifs
          </v-chip>
          
          <!-- Display Sort Method if Active -->
          <v-chip
            v-if="budgetSortOption"
            color="blue-grey"
            small
            class="mr-2"
            outlined
          >
            <v-icon left x-small>mdi-currency-eur</v-icon>
            Budget {{ budgetSortOption === 'asc' ? '↑' : '↓' }}
          </v-chip>
          
          <v-chip
            v-if="commissionSortOption"
            color="blue-grey"
            small
            class="mr-2"
            outlined
          >
            <v-icon left x-small>mdi-cash</v-icon>
            Commission {{ commissionSortOption === 'asc' ? '↑' : '↓' }}
          </v-chip>
          
          <v-btn 
            x-small 
            text 
            color="primary" 
            @click="resetFilters"
            class="ml-auto"
          >
            <v-icon x-small left>mdi-refresh</v-icon>
            Réinitialiser
          </v-btn>
        </div>
      </v-card-subtitle>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "GazOffersFilter",
  props: {
    offers: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isExpanded: false,
      
      // Supplier filter
      selectedSupplier: [],
      suppliers: [], // Will be populated from data
      
      // Duration filter
      selectedDuration: [],
      durations: [], // Will be populated from data
      
      // Offer type filter
      selectedOfferType: [],
      offerTypes: [], // Will be populated from data
      
      // Meter type filter
      selectedMeterType: [],
      meterTypes: [], // Will be populated from data
      
      // Profile filter
      selectedProfile: [],
      profiles: [], // Will be populated from data
      
      // Budget sorting
      budgetSortOption: null,
      
      // Commission sorting
      commissionSortOption: null,
      
      // Date filters
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.selectedSupplier.length > 0 ||
        this.selectedDuration.length > 0 ||
        this.selectedOfferType.length > 0 ||
        this.selectedMeterType.length > 0 ||
        this.selectedProfile.length > 0 ||
        this.budgetSortOption !== null ||
        this.commissionSortOption !== null ||
        this.startDate !== null ||
        this.endDate !== null
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.selectedSupplier.length > 0) count++;
      if (this.selectedDuration.length > 0) count++;
      if (this.selectedOfferType.length > 0) count++;
      if (this.selectedMeterType.length > 0) count++;
      if (this.selectedProfile.length > 0) count++;
      if (this.budgetSortOption !== null) count++;
      if (this.commissionSortOption !== null) count++;
      if (this.startDate !== null) count++;
      if (this.endDate !== null) count++;
      return count;
    }
  },
  watch: {
    offers: {
      immediate: true,
      handler(newOffers) {
        this.extractFilterOptions(newOffers);
      }
    }
  },
  methods: {
    extractFilterOptions(offers) {
      if (!offers || !offers.data) return;
      
      // Create arrays to store all unique values
      const allSuppliers = [];
      const allDurations = [];
      const allOfferTypes = [];
      const allMeterTypes = [];
      const allProfiles = [];
      
      // Loop through all compteurs
      Object.keys(offers.data).forEach(compteurKey => {
        const compteur = offers.data[compteurKey];
        if (compteur && compteur.result && Array.isArray(compteur.result)) {
          // Loop through all offers for this compteur
          compteur.result.forEach(offer => {
            // Skip error offers
            if (offer.status === 'error' || offer.status === 'enattend') return;
            
            // Collect supplier
            if (offer.fournisseur && !allSuppliers.includes(offer.fournisseur)) {
              allSuppliers.push(offer.fournisseur);
            }
            
            // Collect duration - ensure it's treated as a number
            if (offer.DUREE && !allDurations.includes(parseInt(offer.DUREE))) {
              allDurations.push(parseInt(offer.DUREE));
            }
            
            // Collect offer type (can be in 'type' or 'Offre' field)
            const offerType = offer.type || offer.Offre;
            if (offerType && !allOfferTypes.includes(offerType)) {
              allOfferTypes.push(offerType);
            }
            
            // Collect meter type
            if (offer.typeCompteur && !allMeterTypes.includes(offer.typeCompteur)) {
              allMeterTypes.push(offer.typeCompteur);
            }
            
            // Collect profile
            if (offer.profilCompteur && !allProfiles.includes(offer.profilCompteur)) {
              allProfiles.push(offer.profilCompteur);
            }
          });
        }
      });
      
      // Sort numeric arrays
      allDurations.sort((a, b) => a - b);
      
      // Sort text arrays alphabetically
      allSuppliers.sort();
      allOfferTypes.sort();
      allMeterTypes.sort();
      allProfiles.sort();
      
      // Convert to item arrays for v-select
      this.suppliers = allSuppliers.map(supplier => ({
        text: supplier,
        value: supplier
      }));
      
      this.durations = allDurations.map(duration => ({
        text: `${duration} mois`,
        value: duration
      }));
      
      this.offerTypes = allOfferTypes.map(type => ({
        text: type,
        value: type
      }));
      
      this.meterTypes = allMeterTypes.map(type => ({
        text: type,
        value: type
      }));
      
      this.profiles = allProfiles.map(profile => ({
        text: profile,
        value: profile
      }));
    },
    
    onBudgetSortChange(value) {
      // If budget sort is activated, reset commission sort
      if (value !== null) {
        this.commissionSortOption = null;
      }
    },
    
    onCommissionSortChange(value) {
      // If commission sort is activated, reset budget sort
      if (value !== null) {
        this.budgetSortOption = null;
      }
    },
    
    applyFilters() {
      const filters = {
        suppliers: this.selectedSupplier,
        durations: this.selectedDuration,
        offerTypes: this.selectedOfferType,
        meterTypes: this.selectedMeterType,
        profiles: this.selectedProfile,
        budgetSort: this.budgetSortOption,
        commissionSort: this.commissionSortOption,
        startDate: this.startDate,
        endDate: this.endDate
      };
      
      this.$emit('filter-applied', filters);
      
      // Auto collapse the filter panel after applying
      this.isExpanded = false;
    },
    
    resetFilters() {
      this.selectedSupplier = [];
      this.selectedDuration = [];
      this.selectedOfferType = [];
      this.selectedMeterType = [];
      this.selectedProfile = [];
      this.budgetSortOption = null;
      this.commissionSortOption = null;
      this.startDate = null;
      this.endDate = null;
      
      this.applyFilters();
    }
  }
};
</script>

<style scoped>
.filters-container {
  position: relative;
  z-index: 2;
  margin-bottom: 20px;
}

.filter-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.filter-header {
  cursor: pointer;
  padding: 16px 20px;
  background-color: #f5f7fa;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: #eef1f6;
}

.filter-select {
  margin-bottom: 12px;
}

.sort-toggle {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-actions {
  background-color: #f9fafb;
}

.active-filters-indicator {
  background-color: #f5f7fa;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
</style>